import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import { useHistory } from "react-router-dom";
import { FormHelper } from "utils";
import useMessage from "hooks/useMessage";
import useExceptionHandler from "hooks/useExceptionHandler";
import { InputSidebarContainer } from "components";
import ModalContainer from "components/ModalContainer/ModalContainer";
import Attachments from "components/Attachment/Attachment";
import EditHeader from "components/PantallasEdit/Header/EditHeader";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import EditContainer from "components/PantallasEdit/EditContainer/EditContainer";
import InputContainer from "components/PantallasEdit/InputContainer/InputContainer";
import useQueryParams from "hooks/useQueryParams";
import { Compania } from "models/Compania";
import { CompaniaManager, TenantManager, SolicitudCompaniaTenantManager as SolicitudManager, CredencialesCertificacionManager } from "managers";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import { useTheme, useAuthentication } from "hooks";
import DropZone from "components/DropZone/Dropzone";
import {
  TIPO_SOLICITUD_ACTIVACION,
  ESTATUS_PENDIENTE
}
  from "models/SolicitudesCompaniaTenant"
import { WarningException } from "utils";

export default function CompaniaVinculadaEdit({ showLoading, hideLoading }) {
  const [compania, setCompania] = useState({});
  const [credenciales, setCredenciales] = useState({});
  const [tituloHeader, setTituloHeader] = useState("Crear Compañia");
  const [mostrarConfiguracionSistemaExterno, setMostrarConfiguracionSistemaExterno] = useState(false);
  const [showAdjuntarDocumentosModal, setShowAdjuntarDocumentosModal] = useState();
  const [modal, setModal] = useState(false);
  const [isReadonly, setIsReadonly] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(0);

  const { usuario, tienePermiso } = useAuthentication();

  const URLParameters = useQueryParams();
  const requestId = URLParameters.get("requestId");
  const companiaId = URLParameters.get("companiaId");
  const esNuevoRequest = !requestId && !companiaId;
  const esCompaniaDirecta = !!companiaId;
  const { Message, cerrarAlertas, Types, Warning } = useMessage();
  const { handleException } = useExceptionHandler();
  const { theme, vendorTheme } = useTheme();
  const history = useHistory();

  useEffect(() => {
    if (!usuario?.Compania?.EsTenant) {
      Warning({
        message: `Usuario no tiene permiso.`
      });
      return;
    }

    if (requestId) {
      showLoading();
      cargarCompania(requestId, true);
      setTituloHeader("Editar Solicitud");
    } else if (companiaId) {
      showLoading();
      cargarCompania(companiaId, false);
      setTituloHeader("Editar Compañía");
    } else {
      setDefaultValues();
      cerrarAlertas();
    }
    hideLoading();
    return () => { };
  }, [requestId, companiaId]);

  const onFileChange = (propFile, propName) => {
    const nuevasCredenciales = { ...credenciales, [propName]: propFile };
    setCredenciales(nuevasCredenciales);

    const certificadoEnviado = Array.isArray(propFile)
      ? propFile.length > 0
      : !!propFile;

    setCompania((prev) => ({
      ...prev,
      CertificadoEnviado: certificadoEnviado,
    }));
  };

  const setDefaultValues = () => {
    let color1 = "#F16101";
    let color2 = "#8AA8B0";
    let color3 = "#FFFFFF";

    if (theme.title == "GN ECF") {
      color1 = "#D52B1E";
      color2 = "#1097AF";
      color3 = "#FFFFFF";
    }

    setCompania({
      ...Compania,
      Estatus: Compania.DEFAULT_ESTATUS_VALUE,
      Ambiente: Compania.DEFAULT_AMBIENTE_VALUE,
      Nombre: "",
      RazonSocial: "",
      Codigo: "",
      RNC: "",
      DireccionLinea1: "",
      TelefonoPrincipal: "",
      TelefonoSecundario: "",
      Email: "",
      Color1: color1,
      Color2: color2,
      Color3: color3,
      PaginaWeb: "",
      Extension1: "",
      Extension2: "",
      Fax: "",
      ConfiguracionSistemaExterno: "",
      ClienteId: 0,
      EsPrincipal: false,
      Certificada: false,
      EstadoCertificacion: Compania.ESTADO_CERTIFICACION_PENDIENTE,
    });
    setMostrarConfiguracionSistemaExterno(false);
  };

  const cargarCompania = (Id, esRequest) => {
    if (esRequest) {
      SolicitudManager
        .buscarSolicitudCompaniaTenantPorId({ IdSec: requestId, CompaniaId: usuario.CompaniaId })
        .then((data) => {
          data.compania.Color1 = "#" + data.compania.Color1;
          data.compania.Color2 = "#" + data.compania.Color2;
          data.compania.Color3 = "#" + data.compania.Color3;
          setCompania({ ...data.compania });

          const isReadonly = data.solicitud.Estatus != ESTATUS_PENDIENTE
          setIsReadonly(isReadonly);
        });
    } else {
      CompaniaManager
        .buscarPorId(Id)
        .then((data) => {
          data.Color1 = "#" + data.Color1;
          data.Color2 = "#" + data.Color2;
          data.Color3 = "#" + data.Color3;

          setCompania({ ...data, Id });
        });
    }
  };

  const actualizarFlagsCredenciales = (payload) => {
    const rncEnviado = `${payload.RNCCedula}`.trim().length > 0;
    const passwordEnviado = `${payload.Contrasena}`.trim().length > 0;
    const keyCertificadoEnviado = `${payload.KeyCertificado}`.trim().length > 0;
    const certificadoEnviado = Array.isArray(payload.Certificado)
      ? payload.Certificado.length > 0
      : !!payload.Certificado;

    setCompania((prev) => ({
      ...prev,
      RncEnviado: rncEnviado,
      PasswordEnviado: passwordEnviado,
      KeyCertificadoEnviado: keyCertificadoEnviado,
      CertificadoEnviado: certificadoEnviado,
    }));

    return rncEnviado && passwordEnviado && keyCertificadoEnviado && certificadoEnviado;
  };

  const enviarCredenciales = (payload) => {
    const data = payload || {
      RNCCedula: credenciales.RnccedulaCertificacion || "",
      Contrasena: credenciales.PasswordCertificacion || "",
      KeyCertificado: credenciales.KeyCertificado || "",
      Certificado: credenciales.Certificado || [],
    };

    const credencialesCompletas = actualizarFlagsCredenciales(data);
    if (!credencialesCompletas) {
      return;
    }

    showLoading?.();
    CredencialesCertificacionManager.enviar(data)
      .then(() => {
        Message({
          message: "Credenciales enviadas correctamente.",
          autoDismiss: 1.5,
        });
        setCredenciales({});
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading?.());
  };

  function grabar() {
    showLoading();
    if (esNuevoRequest) {
      const companiaNueva = {
        ...compania,
        CompaniaTenantId: usuario.CompaniaId
      };

      setCompania(companiaNueva);
      const formularioJson = JSON.stringify(companiaNueva);

      const solicitudActivacion = {
        TipoSolicitud: TIPO_SOLICITUD_ACTIVACION,
        FormularioJson: formularioJson
      };

      SolicitudManager
        .crearCompaniaVinculada(solicitudActivacion)
        .then((_res) => {
          Message({ message: `Solicitud creada exitosamente` });
          let urlBase = `/CompaniaVinculadaEdit?requestId=${_res.Data.IdSec}`;
          history.push(urlBase);
        })
        .catch((e) => {
          handleException(e)
          return;
        })
        .finally(() => {
          hideLoading();
        });
    } else if (esCompaniaDirecta) {
      CompaniaManager
        .editarVinculada(compania)
        .then(() => {
          Message({ message: `Actualizado Exitosamente` });
          cargarCompania(companiaId, false);
        })
        .catch((e) => handleException(e))
        .finally(() => {
          hideLoading();
        });
    } else {
      const formularioJsonEdit = JSON.stringify(compania);

      const solicitudEditRequest = {
        IdSec: requestId,
        CompaniaId: usuario.CompaniaId,
        TipoSolicitud: TIPO_SOLICITUD_ACTIVACION,
        FormularioJson: formularioJsonEdit
      };

      SolicitudManager
        .editarSolicitudCompaniaTenant(solicitudEditRequest)
        .then(() => {
          Message({ message: `Actualizado Exitosamente` });
          cargarCompania(requestId, true);
          const payloadCredenciales = {
            RNCCedula: credenciales.RnccedulaCertificacion || "",
            Contrasena: credenciales.PasswordCertificacion || "",
            KeyCertificado: credenciales.KeyCertificado || "",
            Certificado: credenciales.Certificado || [],
          };

          const credencialesCompletas = actualizarFlagsCredenciales(payloadCredenciales);
          if (credencialesCompletas) {
            enviarCredenciales(payloadCredenciales);
          }
        })
        .catch((e) => handleException(e))
        .finally(() => {
          hideLoading();
        });
    }
  }

  const handleVolverClick = () => {
    let urlBase = `/SolicitudCompaniaTenantList?t=true`;
    if (esCompaniaDirecta) {
      urlBase = `/CompaniaVinculadaList`;
    }

    history.push(urlBase);
  }

  const adjuntarDocumentoModal = (
    <ModalContainer
      showModal={showAdjuntarDocumentosModal}
      size="lg"
      title="Adjuntar Documentos"
      btnGrabar={{
        show: false
      }}
      cerrar={() => {
        setShowAdjuntarDocumentosModal(false);
      }}
    >
      <EditSection>
        <Attachments
          readonly={isReadonly}
          showLoading={() => { }}
          hideLoading={() => { }}
          companiaId={usuario.CompaniaId}
          fuente="SolicitudesCompaniaTenant"
          modulo="ECF"
          referencia={requestId}
          ocultarHeader={true}
          modal={modal}
          setModal={setModal}
          setAttachmentCount={setAttachmentCount}
        />
      </EditSection>
    </ModalContainer>
  )

  return (
    <>
      {showAdjuntarDocumentosModal && adjuntarDocumentoModal}

      <Form action="" className="form-horizontal" method="">
        <Card>
          <EditHeader
            titulo={`${tituloHeader}`}
            volver={true}
            volverFunc={handleVolverClick}
            {...!isReadonly && { grabar: () => grabar() }}
            {...!esNuevoRequest && !esCompaniaDirecta && { adjuntarDocumento: () => setShowAdjuntarDocumentosModal(true) }}
          />
          <EditContainer>
            <EditSection title="Información General">
              <SectionRow>
                <InputContainer label="Id">
                  <Form.Control
                    type="number"
                    name="Id"
                    value={compania.Id}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={true}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer label="Nombre">
                  <Form.Control
                    type="text"
                    name="Nombre"
                    value={compania.Nombre}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer label="Razón Social">
                  <Form.Control
                    type="text"
                    name="RazonSocial"
                    value={compania.RazonSocial}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer label="Código">
                  <Form.Control
                    type="text"
                    name="Codigo"
                    value={compania.Codigo}
                    onChange={(e) =>
                      FormHelper.handleChangeNoEspace(e, setCompania)
                    }
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer label="RNC">
                  <Form.Control
                    type="text"
                    name="RNC"
                    value={compania.RNC}
                    onChange={(e) =>
                      FormHelper.handleChangeSoloNumero(e, setCompania)
                    }
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer label="Dirección">
                  <Form.Control
                    type="text"
                    name="DireccionLinea1"
                    value={compania.DireccionLinea1}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer label="Teléfono Principal">
                  <Form.Control
                    type="text"
                    name="TelefonoPrincipal"
                    value={compania.TelefonoPrincipal}
                    onChange={(e) =>
                      FormHelper.handleChangeSoloNumero(e, setCompania)
                    }
                    disabled={isReadonly}
                  />
                </InputContainer>
                <InputContainer label="Extensión 1">
                  <Form.Control
                    type="text"
                    name="Extension1"
                    value={compania.Extension1}
                    onChange={(e) =>
                      FormHelper.handleChangeSoloNumero(e, setCompania)
                    }
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer label="Teléfono Secundario">
                  <Form.Control
                    type="text"
                    name="TelefonoSecundario"
                    value={compania.TelefonoSecundario}
                    onChange={(e) =>
                      FormHelper.handleChangeSoloNumero(e, setCompania)
                    }
                    disabled={isReadonly}
                  />
                </InputContainer>

                <InputContainer label="Extensión 2">
                  <Form.Control
                    type="text"
                    name="Extension2"
                    value={compania.Extension2}
                    onChange={(e) =>
                      FormHelper.handleChangeSoloNumero(e, setCompania)
                    }
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer label="Correo">
                  <Form.Control
                    type="text"
                    name="Email"
                    value={compania.Email}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                  />
                </InputContainer>
                <InputContainer label="Fax">
                  <Form.Control
                    type="text"
                    name="Fax"
                    value={compania.Fax}
                    onChange={(e) =>
                      FormHelper.handleChangeSoloNumero(e, setCompania)
                    }
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>

              <SectionRow>
                <InputContainer label="Página Web">
                  <Form.Control
                    type="text"
                    name="PaginaWeb"
                    value={compania.PaginaWeb}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    disabled={isReadonly}
                  />
                </InputContainer>

                <InputContainer label="Cantidad de PT">
                  <Form.Control
                    type="number"
                    name="CantidadPT"
                    value={compania.CantidadPT}
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>
            <EditSection title="Certificación">
              <SectionRow>
                <InputContainer label="¿Está Certificada?" labelSize={6}>
                  <div className="d-flex gap-3">
                    <div className="form-check">
                      <Form.Check
                        type="radio"
                        label="Sí"
                        name="Certificada"
                        value={true}
                        checked={compania.Certificada === true}
                        onChange={(e) => {
                          setCompania({ ...compania, Certificada: true });
                        }}
                        disabled={isReadonly}
                        id="radio-si"
                      />
                    </div>
                    <div className="form-check">
                      <Form.Check
                        type="radio"
                        label="No"
                        name="Certificada"
                        value={false}
                        checked={compania.Certificada === false}
                        onChange={(e) => {
                          setCompania({ ...compania, Certificada: false });
                        }}
                        disabled={isReadonly}
                        id="radio-no"
                      />
                    </div>
                  </div>
                </InputContainer>
              </SectionRow>



              {compania.Certificada === false && (
                <>
                  <EditSection>
                    <SectionRow>
                      <InputContainer label="Estado de Certificación">
                        <Select
                          className="react-select primary"
                          classNamePrefix="react-select"
                          name="EstadoCertificacion"
                          isDisabled={usuario.UsuarioCompaniaId !== Compania.Citrus}
                          onChange={(e) => {
                            setCompania({ ...compania, EstadoCertificacion: e.value });
                          }}
                          options={Compania.ESTADO_CERTIFICACION_LIST}
                          value={Compania.ESTADO_CERTIFICACION_LIST.filter(
                            (option) => option.value === (compania.EstadoCertificacion || Compania.ESTADO_CERTIFICACION_PENDIENTE)
                          )}
                        />
                      </InputContainer>
                    </SectionRow>
                  </EditSection>

                  <EditSection title="Credenciales Portal Certificación de DGII" mt={"mt-5"}>

                    <SectionRow>
                      <InputContainer label="RNC / Cédula">
                        <Form.Control
                          type="text"
                          name="RnccedulaCertificacion"
                          value={credenciales.RnccedulaCertificacion || ""}
                          onChange={(e) => {
                            FormHelper.handleChangeSoloNumero(e, setCredenciales)
                            setCompania((prev) => ({
                              ...prev,
                              RncEnviado: `${e.target.value || ""}`.trim().length > 0,
                            }))
                          }}
                          disabled={isReadonly}
                        />
                      </InputContainer>
                    </SectionRow>

                    <SectionRow>
                      <InputContainer label="Contraseña">
                        <Form.Control
                          type="password"
                          autoComplete="off"
                          name="PasswordCertificacion"
                          value={credenciales.PasswordCertificacion || ""}
                          onChange={(e) => {
                            FormHelper.handleChange(e, setCredenciales)
                            setCompania((prev) => ({
                              ...prev,
                              PasswordEnviado: `${e.target.value || ""}`.trim().length > 0,
                            }))
                          }}
                          disabled={isReadonly}
                        />
                      </InputContainer>
                    </SectionRow>
                  </EditSection>

                  <EditSection title="Credenciales Firma Digital (Certificado)" mt={"mt-5"}>
                    <SectionRow>
                      <InputContainer label="Key Certificado">
                        <Form.Control
                          type="password"
                          autoComplete="off"
                          name="KeyCertificado"
                          value={credenciales.KeyCertificado || ""}
                          onChange={(e) => {
                            FormHelper.handleChange(e, setCredenciales)
                            setCompania((prev) => ({
                              ...prev,
                              KeyCertificadoEnviado: `${e.target.value || ""}`.trim().length > 0,
                            }))
                          }}
                          disabled={isReadonly}
                        />
                      </InputContainer>
                    </SectionRow>

                    <SectionRow>
                      <InputContainer label="Certificado" labelSize={2}>
                        <DropZone
                          label="Seleccione un archivo .p12"
                          updateFilesCb={(f) => onFileChange(f, "Certificado")}
                          multiple={false}
                          type="p12"
                        />
                      </InputContainer>
                    </SectionRow>
                  </EditSection>
                </>
              )}


            </EditSection>
            <EditSection title="Color" mt={"mt-5"}>
              <SectionRow triple={true}>
                <InputContainer label="Primario" labelSize={3}>
                  <Form.Control
                    name="Color1"
                    type="color"
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    value={compania.Color1}
                    disabled={isReadonly}
                  />
                </InputContainer>
                <InputContainer
                  label="Secundario"
                  labelSize={3}
                  labelClassName="pl-2 d-flex"
                >
                  <Form.Control
                    name="Color2"
                    type="color"
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    value={compania.Color2}
                    disabled={isReadonly}
                  />
                </InputContainer>
                <InputContainer label="Logo">
                  <Form.Control
                    name="Color3"
                    type="color"
                    onChange={(e) => FormHelper.handleChange(e, setCompania)}
                    value={compania.Color3}
                    disabled={isReadonly}
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>
          </EditContainer>
        </Card>
      </Form>
    </>
  )
}